import { forwardRef, useId } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from './cn';

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  /** Optional secondary line under the label. */
  description?: React.ReactNode;
  disabled?: boolean;
}

export type RadioOrientation = 'vertical' | 'horizontal';

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Primary label shown next to the control. */
  label?: React.ReactNode;
  /** Optional secondary line under the label. */
  description?: React.ReactNode;
  /** Whether this radio is selected. */
  checked?: boolean;
  /** Class applied to the outer option container (the card). */
  containerClassName?: string;
}

/**
 * Single radio option — a visually-hidden native <input type="radio"> paired with a
 * custom circle and a card-style container. Exported for advanced/standalone use;
 * normally consumed through <RadioGroup />. Forwards the ref to the native input.
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      description,
      checked,
      disabled,
      className,
      containerClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'group relative flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all duration-200',
          checked
            ? 'border-primary/40 bg-primary/5'
            : 'border-line bg-surface hover:border-primary/40 hover:bg-primary/5',
          disabled && 'cursor-not-allowed opacity-50 hover:border-line hover:bg-surface',
          'focus-within:ring-2 focus-within:ring-primary/40',
          containerClassName,
        )}
      >
        <input
          ref={ref}
          id={inputId}
          type="radio"
          checked={checked}
          disabled={disabled}
          className={cn('peer sr-only', className)}
          {...props}
        />

        {/* Custom 20px circle */}
        <span
          aria-hidden="true"
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 bg-surface transition-colors duration-200',
            checked ? 'border-primary' : 'border-line group-hover:border-primary/60',
          )}
        >
          <AnimatePresence initial={false}>
            {checked && (
              <motion.span
                key="dot"
                className="h-2.5 w-2.5 rounded-full bg-primary"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </AnimatePresence>
        </span>

        {(label || description) && (
          <span className="min-w-0 flex-1 select-none">
            {label && (
              <span className="block text-sm font-medium text-content">{label}</span>
            )}
            {description && (
              <span className="mt-0.5 block text-xs text-muted">{description}</span>
            )}
          </span>
        )}
      </label>
    );
  },
);

Radio.displayName = 'Radio';

export interface RadioGroupProps {
  /** The options rendered as selectable cards. */
  options: RadioOption[];
  /** Currently selected value. */
  value: string;
  /** Called with the newly selected value. */
  onChange: (value: string) => void;
  /** Shared name for the underlying native radios (auto-generated when omitted). */
  name?: string;
  /** Group label rendered above the options. */
  label?: React.ReactNode;
  /** Layout direction of the options. */
  orientation?: RadioOrientation;
  /** Disables every option in the group. */
  disabled?: boolean;
  /** Class applied to the outer group container. */
  className?: string;
}

/**
 * Radio group — replaces MUI <RadioGroup> + <FormControlLabel> + <Radio>.
 * Renders a FormField-style group label and a set of card-style options.
 * Keyboard navigation is handled natively via the shared radio name.
 */
export function RadioGroup({
  options,
  value,
  onChange,
  name,
  label,
  orientation = 'vertical',
  disabled,
  className,
}: RadioGroupProps) {
  const reactId = useId();
  const groupName = name ?? reactId;

  return (
    <div
      role="radiogroup"
      aria-label={typeof label === 'string' ? label : undefined}
      className={cn('flex flex-col gap-2', className)}
    >
      {label && (
        <span className="text-sm font-medium text-content">{label}</span>
      )}

      <div
        className={cn(
          'flex gap-2',
          orientation === 'horizontal' ? 'flex-row flex-wrap' : 'flex-col',
        )}
      >
        {options.map((option) => {
          const isDisabled = disabled || option.disabled;
          return (
            <Radio
              key={option.value}
              name={groupName}
              value={option.value}
              label={option.label}
              description={option.description}
              checked={value === option.value}
              disabled={isDisabled}
              onChange={() => {
                if (!isDisabled) onChange(option.value);
              }}
              containerClassName={orientation === 'horizontal' ? 'flex-1' : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}
